import { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AnalyticsRouteTracker } from "./components/AnalyticsRouteTracker/AnalyticsRouteTracker";
import { ScrollToTop } from "./components/ScrollToTop/ScrollToTop";
import { AppLayout } from "./layouts/AppLayout";
import { LegacyRedirect } from "./components/LegacyRedirect";
import { ProjectRedirect } from "./components/ProjectRedirect";
import { PortfolioQueryRedirect } from "./components/PortfolioQueryRedirect";
// Home is eager — avoids an extra chunk round-trip after the entry script loads.
import { ElementLight } from "./screens/ElementLight/ElementLight";

const About = lazy(() =>
  import("./screens/About/About").then((m) => ({ default: m.About })),
);
const Services = lazy(() =>
  import("./screens/Services/Services").then((m) => ({ default: m.Services })),
);
const OfferingDetail = lazy(() =>
  import("./screens/Services/OfferingDetail").then((m) => ({ default: m.OfferingDetail })),
);
const Contact = lazy(() =>
  import("./screens/Contact/Contact").then((m) => ({ default: m.Contact })),
);
const StartProject = lazy(() =>
  import("./screens/StartProject/StartProject").then((m) => ({ default: m.StartProject })),
);
const Projects = lazy(() =>
  import("./screens/Projects/Projects").then((m) => ({ default: m.Projects })),
);
const ProjectDetail = lazy(() =>
  import("./screens/Projects/ProjectDetail").then((m) => ({ default: m.ProjectDetail })),
);
const NotFound = lazy(() =>
  import("./screens/NotFound/NotFound").then((m) => ({ default: m.NotFound })),
);
const FaqHome = lazy(() =>
  import("./screens/Faq/FaqHome").then((m) => ({ default: m.FaqHome })),
);
const FaqTools = lazy(() =>
  import("./screens/Faq/FaqTools").then((m) => ({ default: m.FaqTools })),
);
const FaqCategory = lazy(() =>
  import("./screens/Faq/FaqCategory").then((m) => ({ default: m.FaqCategory })),
);
const FaqDetail = lazy(() =>
  import("./screens/Faq/FaqDetail").then((m) => ({ default: m.FaqDetail })),
);
const Resume = lazy(() =>
  import("./screens/Resume/Resume").then((m) => ({ default: m.Resume })),
);
const Testimonials = lazy(() =>
  import("./screens/Testimonials/Testimonials").then((m) => ({ default: m.Testimonials })),
);
const Offline = lazy(() =>
  import("./screens/Offline/Offline").then((m) => ({ default: m.Offline })),
);
const ExperienceDetail = lazy(() =>
  import("./screens/Experience/ExperienceDetail").then((m) => ({ default: m.ExperienceDetail })),
);
const Search = lazy(() =>
  import("./screens/Search/Search").then((m) => ({ default: m.Search })),
);
const Watch = lazy(() =>
  import("./screens/Watch/Watch").then((m) => ({ default: m.Watch })),
);
const ProjectBriefsAdmin = lazy(() =>
  import("./screens/Admin/ProjectBriefsAdmin").then((m) => ({ default: m.ProjectBriefsAdmin })),
);
const ProjectBriefDetail = lazy(() =>
  import("./screens/Admin/ProjectBriefDetail").then((m) => ({ default: m.ProjectBriefDetail })),
);

function LegacyAiFaqRedirect(): JSX.Element {
  const { id } = useParams<{ id?: string }>();
  return <Navigate to={id ? `/faq/intelligence/${id}` : "/faq/intelligence"} replace />;
}

export default function App() {
  return (
    <Router>
      <AnalyticsRouteTracker />
      <ScrollToTop />
      <Suspense fallback={null}>
        <Routes>
          <Route element={<AppLayout />}>
            <Route path="/" element={<ElementLight />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/products/:slug" element={<OfferingDetail />} />
            <Route path="/services/capabilities/:slug" element={<OfferingDetail />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/start-project" element={<StartProject />} />

            {/* Canonical portfolio URLs (original Jekyll structure) */}
            <Route path="/ux-portfolio" element={<Projects />} />
            <Route path="/ux-portfolio/:category" element={<Projects />} />
            <Route path="/ux-portfolio/:category/:projectSlug" element={<ProjectDetail />} />

            {/* Legacy /projects → canonical */}
            <Route path="/projects" element={<PortfolioQueryRedirect />} />
            <Route path="/projects/:id" element={<ProjectRedirect />} />

            <Route path="/experience" element={<Resume />} />
            <Route path="/experience/:id" element={<ExperienceDetail />} />
            <Route path="/search" element={<Search />} />
            <Route path="/watch/:id" element={<Watch />} />
            <Route path="/testimonials" element={<Testimonials />} />
            <Route path="/faq" element={<FaqHome />} />
            <Route path="/faq/tools" element={<FaqTools />} />
            <Route path="/faq/ai" element={<LegacyAiFaqRedirect />} />
            <Route path="/faq/ai/:id" element={<LegacyAiFaqRedirect />} />
            <Route path="/faq/:category" element={<FaqCategory />} />
            <Route path="/faq/:category/:id" element={<FaqDetail />} />
            <Route path="/offline" element={<Offline />} />
            <Route path="/admin/project-briefs" element={<ProjectBriefsAdmin />} />
            <Route path="/admin/project-briefs/:id" element={<ProjectBriefDetail />} />
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
          </Route>
          {/* Legacy Jekyll paths (old /portfolio/ short URLs only) */}
          <Route path="/what-design-i-do/*" element={<LegacyRedirect />} />
          <Route path="/ux-design-resume/*" element={<LegacyRedirect />} />
          <Route path="/portfolio/*" element={<LegacyRedirect />} />
          <Route path="/contact-your-designer/*" element={<LegacyRedirect />} />
          <Route path="/faq/code/*" element={<LegacyRedirect />} />
        </Routes>
      </Suspense>
    </Router>
  );
}
